import { useTranslation } from 'react-i18next';
import { Flame, LayoutGrid } from 'lucide-react';
import CategoryIcon from './CategoryIcon';
import { POS_CATEGORY_SELECTED_CLASS } from '@/constants/posCategoryIcons';
import type { POSCategory } from '@/types';

interface Props {
  categories: POSCategory[];
  activeCategory: string;
  onSelect: (name: string) => void;
  position: 'left' | 'right';
  showHotSelling?: boolean;
  viewMode?: 'products' | 'services';
  className?: string;
}

export default function PosCategoryPanel({
  categories,
  activeCategory,
  onSelect,
  position,
  showHotSelling = true,
  viewMode = 'products',
  className = '',
}: Props) {
  const { t } = useTranslation();

  const borderClass = position === 'left' ? 'border-r' : 'border-l';

  return (
    <div className={`${borderClass} border-gray-200 bg-white w-56 xl:w-64 flex flex-col h-full shrink-0 ${className}`}>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {showHotSelling && viewMode === 'products' && (
          <button
            type="button"
            onClick={() => onSelect('__recent__')}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-colors min-h-[52px] ${
              activeCategory === '__recent__'
                ? `bg-orange-500 text-white ${POS_CATEGORY_SELECTED_CLASS}`
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Flame className="w-5 h-5" />
            {t('pos.hotSelling')}
          </button>
        )}
        <button
          type="button"
          onClick={() => onSelect('All')}
          className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-colors min-h-[52px] ${
            activeCategory === 'All'
              ? `bg-primary-600 text-white ${POS_CATEGORY_SELECTED_CLASS}`
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <LayoutGrid className="w-5 h-5" />
          {t('common.all')}
        </button>
        {categories.map((cat) => {
          const selected = activeCategory === cat.name;
          const bg = cat.pos_category_color || (selected ? '#2563eb' : '#f3f4f6');
          const colored = Boolean(cat.pos_category_color) || selected;
          return (
            <button
              key={cat.name}
              type="button"
              onClick={() => onSelect(cat.name)}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-colors min-h-[52px] hover:opacity-90 ${
                colored ? 'text-white' : 'text-gray-700'
              } ${selected ? POS_CATEGORY_SELECTED_CLASS : ''}`}
              style={{ backgroundColor: bg }}
              aria-pressed={selected}
            >
              <CategoryIcon
                icon={cat.pos_category_icon}
                image={cat.pos_category_image}
                color={colored ? 'rgba(255,255,255,0.22)' : '#e5e7eb'}
                foreground={colored ? '#ffffff' : undefined}
                size="md"
              />
              <span className="truncate text-left flex-1">{cat.category_name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
